// WWI pillbox — a squat hexagonal concrete machine-gun bunker: battered
// hexagonal wall, a dark horizontal firing slit, a low concrete dome under an
// overhanging cap lip, and a ring of sandbags around the base.
import * as THREE from "three";
import { defineModel, styleV3, type Part } from "../../lib";

const { box, cyl, slab, poles } = styleV3;

const CONCRETE = "#9a9891";
const CONCRETE_DK = "#87857e";
const GUNMETAL = "#4a4d52";
const SLIT = "#26282a";
const CANVAS = "#cbbd93";
const CANVAS_DK = "#b3a67e";

const BASE = 0.12; // foundation slab top

// The battered hexagonal wall, `h` tall, a flat face turned to the front (+z).
function wall(h: number): Part {
  return cyl(0.32, 0.35, h, 6, CONCRETE, [0, BASE + h / 2, 0], [0, Math.PI / 6, 0]);
}

// The dark horizontal firing slit on the front face.
function firingSlit(): Part {
  return box(0.34, 0.06, 0.08, SLIT, [0, BASE + 0.26, 0.28]);
}

// The overhanging cap lip and the low concrete dome on top.
function roof(): Part[] {
  return [
    cyl(0.37, 0.37, 0.07, 6, CONCRETE_DK, [0, BASE + 0.375, 0], [0, Math.PI / 6, 0]),
    { geometry: new THREE.SphereGeometry(0.25, 8, 6), color: CONCRETE,
      position: [0, BASE + 0.42, 0], scale: [1, 0.42, 1] },
    cyl(0.02, 0.02, 0.14, 5, GUNMETAL, [0.13, BASE + 0.5, -0.09]),
  ];
}

// The sandbag ring around the base, bags laid tangentially, tones alternating.
function sandbags(): Part[] {
  const out: Part[] = [];
  const n = 10, r = 0.43;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    out.push(box(0.16, 0.085, 0.1, i % 2 ? CANVAS : CANVAS_DK,
      [Math.cos(a) * r, BASE + 0.043, Math.sin(a) * r],
      [0, -a - Math.PI / 2, 0]));
  }
  // A staggered second course guarding the front quarter.
  for (const a of [Math.PI / 2 - 0.32, Math.PI / 2, Math.PI / 2 + 0.32]) {
    out.push(box(0.15, 0.08, 0.1, CANVAS,
      [Math.cos(a) * (r - 0.015), BASE + 0.125, Math.sin(a) * (r - 0.015)],
      [0, -a - Math.PI / 2, 0]));
  }
  return out;
}

function foundation(): Part {
  return slab(0.92, 0.92);
}

export const pillbox = defineModel({
  id: "pillbox",
  tiles: { w: 1, h: 1 },
  batchId: "wwi",
  name: "Pillbox",
  tags: ["building", "wwi", "military", "defense", "bunker"],
  summary: "A squat hexagonal concrete bunker with a dark firing slit and a sandbag ring.",
  description:
    "A Great-War machine-gun pillbox: a battered hexagonal concrete drum with " +
    "a horizontal firing slit, a low dome under an overhanging cap and a ring " +
    "of sandbags heaped around its base.",
  author: "Fable 5",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "foundation", label: "Foundation", parts: () => [foundation()] },
    {
      id: "lower", label: "Lower",
      parts: () => [foundation(), wall(0.16), ...poles(0.8, 0.8, 0.35)],
    },
    {
      id: "upper", label: "Upper",
      parts: () => [foundation(), wall(0.34), firingSlit(), ...poles(0.8, 0.8, 0.5)],
    },
    {
      id: "complete", label: "Complete",
      parts: () => [foundation(), wall(0.34), firingSlit(), ...roof(), ...sandbags()],
    },
  ],
  animations: [],
});
